/**
 * iOS 静音开关（物理静音键 / 控制中心静音）下 Web Audio 无声的解锁方案
 *
 * 问题：
 *   iOS 上 Web Audio（AudioContext）的输出受系统静音开关控制，
 *   手机处于静音模式时，即使 AudioContext.state === "running"，
 *   用 AudioContext 播放 PCM/TTS 也是无声的。
 *
 * 方案（与 unmute-ios-audio 一致）：
 *   HTML5 <audio>/<video> 元素播放不受静音开关影响，且当页面中存在
 *   正在播放（哪怕是静音内容）的媒体元素时，iOS 会为页面建立
 *   "媒体播放"音频会话，此后 Web Audio 的输出也会路由到该会话，
 *   从而绕过静音开关。
 *
 *   因此：在用户手势（click/touchstart 等）内，创建一个隐藏的
 *   <audio> 元素，循环播放一段极短的静音 WAV，激活媒体会话。
 *   之后 AudioContext 播放的真实音频即可正常出声（音量仍受系统音量键控制）。
 *
 * 注意：
 *   - 仅在 iOS（iPhone/iPad/iPadOS）上启用，其他平台直接透传。
 *   - iOS 自动播放策略要求首次 play() 必须在用户手势内触发，
 *     否则会被拒绝（NotAllowedError），此时会监听下一次手势重试。
 *   - 幂等：可随时调用 ensureUnlocked()，已解锁后直接返回。
 */

// 生成一段极短的静音 WAV data URI（16bit 单声道 8000Hz，约 0.1s）
function createSilentWavDataUri() {
    const sampleRate = 8000;
    const dataSize = Math.round(sampleRate * 0.1) * 2; // 16bit 单声道字节数
    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (offset, text) => {
        for (let i = 0; i < text.length; i++) {
            view.setUint8(offset + i, text.charCodeAt(i));
        }
    };

    writeString(0, "RIFF");
    view.setUint32(4, 36 + dataSize, true);
    writeString(8, "WAVE");
    writeString(12, "fmt ");
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // 单声道
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true); // 字节率
    view.setUint16(32, 2, true); // 块对齐
    view.setUint16(34, 16, true); // 位深
    writeString(36, "data");
    view.setUint32(40, dataSize, true);
    // 剩余数据全部为 0（静音）

    const bytes = new Uint8Array(buffer);
    let binary = "";
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return "data:audio/wav;base64," + btoa(binary);
}

const UA = typeof navigator !== "undefined" ? navigator.userAgent : "";

// 是否 iOS（含 iPadOS 13+，桌面版 Safari 的 iPad 需靠 touch 点数判断）
const IS_IOS =
    /iP(hone|od|ad)/.test(UA) ||
    (typeof navigator !== "undefined" &&
        navigator.platform === "MacIntel" &&
        navigator.maxTouchPoints > 1);

const SILENT_WAV_DATA_URI = typeof window !== "undefined"
    ? createSilentWavDataUri()
    : "";

// 用户手势事件：捕获阶段监听，第一时间拿到用户激活
const GESTURE_EVENTS = ["touchstart", "touchend", "click", "keydown"];

class IOSSilentUnlock {
    constructor() {
        this.audio = null;
        this.unlocked = false;
        this._listenersInstalled = false;
        this._gestureHandler = null;
    }

    /**
     * 确保 iOS 静音开关下 Web Audio 可出声。
     *
     * 幂等操作，可在引擎构造、resume、play 时反复调用：
     * - 首次调用会注册全局用户手势监听（捕获阶段），
     *   任意一次点击/触摸都会尝试解锁；
     * - 若本次调用正处于用户手势内，会立即完成解锁。
     */
    ensureUnlocked() {
        if (!IS_IOS || this.unlocked) {
            return;
        }

        this._installGestureListeners();
        this._attemptUnlock();
    }

    _installGestureListeners() {
        if (this._listenersInstalled) {
            return;
        }
        this._listenersInstalled = true;
        this._gestureHandler = () => this._attemptUnlock();

        for (const eventName of GESTURE_EVENTS) {
            window.addEventListener(eventName, this._gestureHandler, {
                capture: true,
                passive: true
            });
        }
    }

    _removeGestureListeners() {
        if (!this._listenersInstalled) {
            return;
        }
        this._listenersInstalled = false;

        for (const eventName of GESTURE_EVENTS) {
            window.removeEventListener(eventName, this._gestureHandler, {
                capture: true
            });
        }
        this._gestureHandler = null;
    }

    /**
     * 创建并播放隐藏的静音 audio，激活 iOS 媒体播放会话。
     */
    _attemptUnlock() {
        if (!IS_IOS || this.unlocked) {
            return;
        }

        try {
            if (!this.audio) {
                this.audio = document.createElement("audio");
                this.audio.setAttribute("x-webkit-airplay", "deny");
                this.audio.setAttribute("playsinline", "playsinline");
                this.audio.setAttribute("webkit-playsinline", "webkit-playsinline");
                this.audio.loop = true;
                this.audio.preload = "auto";
                this.audio.src = SILENT_WAV_DATA_URI;
                this.audio.style.display = "none";
                (document.body || document.documentElement).appendChild(this.audio);
            }

            // 在用户手势内 play() 成功 → 媒体会话激活 → 解锁
            const playPromise = this.audio.play();

            if (playPromise && typeof playPromise.then === "function") {
                playPromise
                    .then(() => {
                        this.unlocked = true;
                        this._removeGestureListeners();
                    })
                    .catch(() => {
                        // 未在有效手势内（如 NotAllowedError），等待下一次手势重试
                    });
            } else {
                this.unlocked = true;
                this._removeGestureListeners();
            }
        } catch (err) {
            console.warn("[IOSSilentUnlock] unlock failed:", err);
        }
    }
}

// 单例导出
export default new IOSSilentUnlock();
